import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import { useListZones } from 'src/features/zone/api/useListZones';
import { useListPastStayPeriods } from 'src/features/stay/api/useListPastStayPeriods';
import { useListActiveStayPeriods } from 'src/features/stay/api/useListActiveStayPeriods';
import GatewayPassSidePanel from 'src/features/gatewayPass/components/GatewayPassSidePanel';
import { CellStayPeriodsTableActions } from 'src/features/stay/components/cell/CellStayPeriodsTableActions';
import StayPeriodSidePanel from 'src/features/stay/components/dashboard/StayPeriodSidePanel';
import { ActiveStayPeriod, STAY_TYPE, VISITOR_CATEGORY } from 'src/features/stay/types';
import { GATEWAY_PASSES_PANEL } from 'src/features/gatewayPass/utils/constants';

import {
    visitorCategoryBadgeOptions,
    visitorCategoryTranslationMap,
} from 'src/features/visitor/utils/constants';
import { timestampToId } from 'src/features/visitor/utils/timestampToId';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { useGetStayDuration } from 'src/hooks/useGetStayDuration';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellAction } from 'src/registry/components/table/cells/CellAction';
import { CellPrice } from 'src/registry/components/table/cells/CellPrice';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import { useSingleRowSelection } from 'src/registry/hooks/useSingleRowSelection';
import useSorting from 'src/registry/hooks/useSorting';
import CellStayPeriodZones from 'src/features/stay/components/cell/CellStayPeriodZones.tsx';
import { STAY_PERIODS_PANEL } from 'src/features/gatewayPass/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

type StayPeriodsTableProps = {
    stayType?: STAY_TYPE;
};

const columnHelper = createColumnHelper<ActiveStayPeriod>();
const initialSortingOptions = { initialSortingState: [{ id: 'start', desc: true }] };
const initialSearchOptions = { globalFilterKey: 'search' };

const StayPeriodsTable = ({ stayType }: StayPeriodsTableProps) => {
    const { formatMessage, locale } = useIntl();
    const { stayId = '' } = useParams();
    const { getDurationFromStartDateToEndDate } = useGetStayDuration();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch(initialSearchOptions);
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting(initialSortingOptions);

    const { parkingId } = useParking();
    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();
    const [isEntry, setIsEntry] = React.useState(true);

    const singleRowSelection = React.useCallback(
        (rowId: string, isEntry: boolean) => {
            setIsEntry(isEntry);
            setRowSelectionState({ [rowId]: true });
        },
        [setRowSelectionState],
    );

    const { data: listPricingPlans } = useListPricingPlans({ parkingId });

    const {
        data: activeStayPeriodsData,
        isLoading: isLoadingActiveStayPeriod,
        isError: isErrorActiveStayPeriod,
    } = useListActiveStayPeriods({
        parkingId,
        stayId: stayId ?? '',
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        config: { enabled: !!stayId && stayType === STAY_TYPE.ACTIVE },
    });

    const {
        data: pastStayPeriodsData,
        isLoading: isLoadingPastStayPeriods,
        isError: isErrorPastStayPeriodsError,
    } = useListPastStayPeriods({
        parkingId,
        stayId: stayId ?? '',
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        config: { enabled: !!stayId && stayType === STAY_TYPE.PAST },
    });

    const { data: listZonesData } = useListZones({ parkingId });
    const { data: listGatewaysData } = useListGateways({ parkingId });

    const filteredEmptyTranslations = React.useMemo(() => {
        return listZonesData?.results?.filter((zone) => zone.translations.length > 0);
    }, [listZonesData?.results]);

    const zoneFilterOptions = React.useMemo(
        () =>
            filteredEmptyTranslations?.map((zone) => {
                const translatedZone = zone.translations.find((z) => z.language === locale);

                return { value: zone.id, label: translatedZone?.name };
            }),
        [filteredEmptyTranslations, locale],
    );

    const pricingPlanFilterOptions = listPricingPlans?.results.map((plan) => {
        const translatedPlan = plan?.translations.find((g) => g.language === locale);
        return { value: plan?.id, label: translatedPlan?.name };
    });

    const gatewayFilterOptions = listGatewaysData?.results.map((gateway) => {
        const translatedGateway = gateway?.translations.find((g) => g.language === locale);

        return { value: gateway?.id, label: translatedGateway?.name };
    });

    const tableData = stayType === STAY_TYPE.ACTIVE ? activeStayPeriodsData : pastStayPeriodsData;

    const [selectedZoneGatewayPass, setSelectedZoneGatewayPass] = React.useState<string>('')
    const gatewayPassId = React.useMemo(() => {
        if (selectedZoneGatewayPass) {
            return selectedZoneGatewayPass;
        }
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!tableData || !firstSelected) return '';
        const result = tableData.results.find((row) => row.id === firstSelected);
        return isEntry ? result?.entry_pass?.id : result?.exit_pass?.id;
    }, [isEntry, tableData, rowSelectionState, selectedZoneGatewayPass]);

    const columns = [
        columnHelper.accessor('number', {
            header: DataTableColumnHeader,
            cell: (ctx) => (
                <CellAction
                    {...ctx}
                    sidePanelName={STAY_PERIODS_PANEL}
                    singleRowSelection={(rowId) => singleRowSelection(rowId, false)}
                    render={() => (
                        <div className="flex flex-col">
                            <span>{ctx.getValue()}</span>
                        </div>
                    )}
                />
            ),
            enableSorting: true,
            meta: {
                title: '#',
            },
            maxSize: 25,
        }),
        columnHelper.accessor('start', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(visitorMessages.start),
            },
        }),
        columnHelper.accessor('end', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(visitorMessages.end),
            },
        }),
        columnHelper.accessor('duration', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) =>
                getDurationFromStartDateToEndDate({
                    startDate: row.original.start,
                    endDate: row.original.end ?? undefined,
                }),
            meta: {
                title: formatMessage(visitorMessages.duration),
            },
        }),
        columnHelper.accessor('stay.period_visitor_category', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (props) => <CellBadge badgeOptions={visitorCategoryBadgeOptions} {...props} />,
            meta: {
                title: formatMessage(visitorMessages.type),
                badgeOptions: visitorCategoryBadgeOptions,
            },
        }),
        columnHelper.accessor('zones', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const visitorCategory = row.original.stay.period_visitor_category;
                const visitorCategoryTranslation = visitorCategory
                    ? formatMessage(visitorCategoryTranslationMap[visitorCategory as VISITOR_CATEGORY])
                    : '-'

                return <CellStayPeriodZones zonesData={row.original?.zones} visitorCategory={visitorCategoryTranslation} />;
            },
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(visitorMessages.zones),
                options: zoneFilterOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
            id: 'zones',
        }),
        columnHelper.accessor('pricing_plan.translations', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const pricingPlanName =
                    row.original.pricing_plan?.translations?.find((zone) => zone.language === locale)?.name ?? '-';
                return <span className="font-medium whitespace-nowrap">{pricingPlanName}</span>;
            },
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(visitorMessages.pricingPlan),
                options: pricingPlanFilterOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
            id: 'pricing_plan',
        }),

        columnHelper.accessor('due_amount_with_tax', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (prop) => <CellPrice currency={prop.row.original.pricing_plan?.currency} {...prop} />,
            meta: {
                title: formatMessage(visitorMessages.amountDue),
            },
        }),
        columnHelper.accessor('entry_pass', {
            header: DataTableColumnHeader,
            cell: (props) => {
                const entryName =
                    props.row.original.entry_pass?.gateway?.translations?.find((entry) => entry.language === locale)
                        ?.name ?? '-';
                return (
                    <CellAction
                        {...props}
                        sidePanelName={GATEWAY_PASSES_PANEL}
                        singleRowSelection={(rowId) => singleRowSelection(rowId, true)}
                        render={() => (
                            <span className="flex flex-col">
                                <span className="font-medium text-gray-900">{entryName}</span>
                                <span className="text-primary-600 font-normal">
                                    {timestampToId(props.row.original.start)}
                                </span>
                            </span>
                        )}
                    />
                );
            },
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(visitorMessages.entry),
                options: gatewayFilterOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
                filterLabel: formatMessage(visitorMessages.gateway),
            },
            id: 'gateway',
        }),
        columnHelper.accessor('exit_pass', {
            header: DataTableColumnHeader,
            cell: ({ row, ...props }) => {
                const exitName = row.original.exit_pass?.gateway?.translations?.find(
                    (exit) => exit.language === locale,
                )?.name;
                const exitNameDisplay = exitName ?? '-';
                return (
                    <CellAction
                        {...props}
                        row={row}
                        sidePanelName={GATEWAY_PASSES_PANEL}
                        singleRowSelection={(rowId) => singleRowSelection(rowId, false)}
                        render={() => (
                            <span className="flex flex-col">
                                <span className="font-medium text-gray-900">{exitNameDisplay}</span>
                                <span className="text-primary-600 font-normal">
                                    {exitName ? timestampToId(row.original.end) : ''}
                                </span>
                            </span>
                        )}
                    />
                );
            },
            enableHiding: true,
            meta: {
                title: formatMessage(visitorMessages.exit),
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => (
                <CellStayPeriodsTableActions
                    {...props}
                    sidePanelName={STAY_PERIODS_PANEL}
                    singleRowSelection={(rowId) => singleRowSelection(rowId, false)}
                />
            ),
        }),
    ];

    const selectedPeriod = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!tableData || !firstSelected) return;
        return tableData.results.find((row) => row.id === firstSelected);
    }, [tableData, rowSelectionState]);
    return (
        <>
            <DataTable
                columns={columns as any}
                data={tableData}
                pagination={pagination}
                search={search}
                sorting={sorting}
                filters={filters}
                semantics={{
                    title: formatMessage(visitorMessages.stayPeriods),
                    entryName: formatMessage(visitorMessages.period),
                    entryNames: formatMessage(visitorMessages.periods),
                    placeholder: formatMessage(visitorMessages.number),
                }}
                isError={isErrorActiveStayPeriod || isErrorPastStayPeriodsError}
                isLoading={isLoadingActiveStayPeriod || isLoadingPastStayPeriods}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
                enableSearchParams={false}
            />
            <SidePanel onClose={clearSelection} name={STAY_PERIODS_PANEL}>
                <StayPeriodSidePanel
                    selectedStayId={selectedPeriod?.stay.id}
                    periodId={selectedPeriod?.id}
                    stayType={stayType}
                    setSelectedZoneGatewayPass={setSelectedZoneGatewayPass}
                />
            </SidePanel>
            <SidePanel onClose={() => {
                clearSelection();
                setSelectedZoneGatewayPass('');
            }} name={GATEWAY_PASSES_PANEL}>
                <GatewayPassSidePanel gatewayPassId={gatewayPassId} />
            </SidePanel>
        </>
    );
};

export default StayPeriodsTable;
